import { Tooltip, TooltipContent, TooltipTrigger, cn } from '@parcel/ui';
import type * as React from 'react';

export interface SpecToolRowShellProps {
  title: string;
  /** Hover copy: what the tool does, then whatever metadata the caller has. */
  tooltip: { description: string; details?: readonly string[] };
  /** The controls for this row; the caller owns how many and in what order. */
  children: React.ReactNode;
  testId?: string;
  className?: string;
}

/**
 * One line of a tools list: the tool on the left, whatever controls it on the right.
 *
 * Shared by the Spec tools settings list and the connector detail sheet so the two read as
 * the same list. Only the chrome lives here -- the settings row shows one decision, a
 * workspace connector row shows the shared default and the member's own, and neither belongs
 * to the other.
 */
export function SpecToolRowShell({
  title,
  tooltip,
  children,
  testId = 'spec-tool-row',
  className,
}: SpecToolRowShellProps) {
  return (
    <div
      className={cn('flex items-center justify-between gap-4 py-2.5', className)}
      data-testid={testId}
    >
      <div className="flex min-w-0 items-center gap-2">
        {/*
          `asChild` so the trigger IS the title span rather than a button wrapping it: a row
          label is not an activatable control, and nesting one would add a tab stop per row
          that does nothing.
        */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="truncate text-sm" data-testid={`${testId}-title`}>
              {title}
            </span>
          </TooltipTrigger>
          <TooltipContent side="right" align="start" className="max-w-sm space-y-1 p-3">
            <p className="text-xs text-popover-foreground">{tooltip.description}</p>
            {tooltip.details && tooltip.details.length > 0 ? (
              <div className="space-y-0.5">
                {tooltip.details.map((detail) => (
                  <p key={detail} className="text-xs text-muted-foreground">
                    {detail}
                  </p>
                ))}
              </div>
            ) : null}
          </TooltipContent>
        </Tooltip>
      </div>

      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}
